/** Browser session cache. New logins keep bearer tokens in HttpOnly cookies.
 * Storage holds only a user cache and cookie-session marker. Legacy bearer
 * sessions remain readable until the next login, logout or expiry. */

const ACCESS_KEY = 'friday:access'
const REFRESH_KEY = 'friday:refresh'
const USER_KEY = 'friday:user'
const COOKIE_KEY = 'friday:cookie-session'
const SESSION_KEYS = [ACCESS_KEY, REFRESH_KEY, USER_KEY, COOKIE_KEY] as const

export interface AuthUser {
  id: string
  email: string
  displayName: string
  avatarUrl?: string | null
  /** Per-user override; falls back to displayName. */
  alias?: string | null
  /** Per-user override; falls back to avatarUrl. */
  customAvatarUrl?: string | null
}

export interface AuthTokens {
  cookieSession?: boolean
  accessToken: string
  refreshToken: string
  user: AuthUser
}

export interface SessionOptions {
  /** Keep the existing auth tokens after the browser closes. Defaults to false. */
  remember?: boolean
}

let inMemoryAccess: string | null = null
let inMemoryRefresh: string | null = null
let inMemoryUser: AuthUser | null = null
let inMemoryCookie = false
let inMemoryRemembered = false
// Changes on login, logout, hydration, or refresh. Async callers use this to
// avoid resurrecting credentials after a logout or account switch.
let sessionGeneration = 0

function getStore(kind: 'local' | 'session'): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage
  } catch {
    return null
  }
}

function removeSession(store: Storage | null): void {
  if (!store) return
  try {
    for (const key of SESSION_KEYS) store.removeItem(key)
  } catch {}
}

function readSession(store: Storage | null): AuthTokens | null {
  if (!store) return null
  try {
    const accessToken = store.getItem(ACCESS_KEY)
    const refreshToken = store.getItem(REFRESH_KEY)
    const userJson = store.getItem(USER_KEY)
    if (store.getItem(COOKIE_KEY) === '1' && userJson)
      return {
        accessToken: '',
        refreshToken: '',
        user: JSON.parse(userJson) as AuthUser,
        cookieSession: true,
      }
    if (!accessToken || !refreshToken || !userJson) return null
    return { accessToken, refreshToken, user: JSON.parse(userJson) as AuthUser }
  } catch {
    return null
  }
}

/**
 * Reads exactly one persisted source. Old Friday localStorage sessions remain
 * remembered for backwards compatibility; any duplicate sessionStorage copy is
 * removed so refreshes cannot select a stale token source.
 */
export function getStoredSession(): AuthTokens | null {
  if (inMemoryUser && inMemoryCookie) {
    return { accessToken: '', refreshToken: '', user: inMemoryUser, cookieSession: true }
  }
  if (inMemoryAccess && inMemoryRefresh && inMemoryUser) {
    return { accessToken: inMemoryAccess, refreshToken: inMemoryRefresh, user: inMemoryUser }
  }
  const local = getStore('local')
  const session = getStore('session')
  const remembered = readSession(local)
  const temporary = readSession(session)
  const selected = remembered ?? temporary
  if (!selected) return null

  const cookie = selected.cookieSession === true
  const changed =
    inMemoryAccess !== selected.accessToken ||
    inMemoryRefresh !== selected.refreshToken ||
    inMemoryCookie !== cookie
  inMemoryAccess = selected.accessToken
  inMemoryRefresh = selected.refreshToken
  inMemoryUser = selected.user
  inMemoryCookie = cookie
  inMemoryRemembered = remembered != null
  if (changed) sessionGeneration += 1
  removeSession(remembered ? session : local)
  return selected
}

/** A monotonic identity for the currently authenticated browser session. */
export function getSessionGeneration(): number {
  return sessionGeneration
}

/** True only while the given credentials remain the active auth scope. */
export function isCurrentSession(session: AuthTokens, generation: number): boolean {
  return (
    sessionGeneration === generation &&
    inMemoryAccess === session.accessToken &&
    inMemoryRefresh === session.refreshToken &&
    inMemoryCookie === (session.cookieSession === true)
  )
}

function writeSession(store: Storage, tokens: AuthTokens): boolean {
  try {
    if (tokens.cookieSession) {
      store.removeItem(ACCESS_KEY)
      store.removeItem(REFRESH_KEY)
      store.setItem(COOKIE_KEY, '1')
    } else {
      store.removeItem(COOKIE_KEY)
      store.setItem(ACCESS_KEY, tokens.accessToken)
      store.setItem(REFRESH_KEY, tokens.refreshToken)
    }
    store.setItem(USER_KEY, JSON.stringify(tokens.user))
    return true
  } catch {
    // Do not restore a previous identity after a new login was selected. A
    // blocked store leaves the current tab memory-only instead of reviving an
    // old account on its next reload.
    removeSession(store)
    return false
  }
}

export function setSession(tokens: AuthTokens, options: SessionOptions = {}): void {
  const remember = options.remember ?? inMemoryRemembered
  inMemoryAccess = tokens.accessToken
  inMemoryRefresh = tokens.refreshToken
  inMemoryUser = tokens.user
  inMemoryCookie = tokens.cookieSession === true
  inMemoryRemembered = remember
  sessionGeneration += 1

  const target = getStore(remember ? 'local' : 'session')
  const other = getStore(remember ? 'session' : 'local')
  // The user's new scope wins even when the target store is blocked. Clear the
  // opposite persisted scope first so an old account cannot reappear on reload.
  removeSession(other)
  if (target) writeSession(target, tokens)
}

/** Clears both temporary and remembered credentials on logout or expiry. */
export function clearSession(): void {
  inMemoryAccess = null
  inMemoryRefresh = null
  inMemoryUser = null
  inMemoryCookie = false
  inMemoryRemembered = false
  sessionGeneration += 1
  removeSession(getStore('local'))
  removeSession(getStore('session'))
}

export function getAccessToken(): string | null {
  if (inMemoryAccess) return inMemoryAccess
  return getStoredSession()?.accessToken || null
}

/**
 * Synchronous session check. Prefers the in-memory copy; falls back to the
 * selected browser store. Use in router guards and other synchronous callers.
 */
export function hasSession(): boolean {
  if (inMemoryUser != null) return true
  return getStoredSession() != null
}

export function getUser(): AuthUser | null {
  return inMemoryUser ?? getStoredSession()?.user ?? null
}

/** Patch the stored user (e.g. after a /me PATCH) without losing tokens. */
export function patchStoredUser(patch: Partial<AuthUser>): void {
  const cur = getStoredSession()
  if (!cur) return
  setSession({ ...cur, user: { ...cur.user, ...patch } })
}
